// eslint-disable-next-line
import {injectable} from "inversify";
import { IApiResponse, IHttpService } from "./http-service";
import { TYPE, rootContainer } from "./container/root";



export enum PRODUCT_STATUS_ENUM {
    RETURNED = 'returned',
    REJECTED = 'rejected',
    NOT_ELIGIBLE = 'not-eligible',
    VIRTUAL_GOOD = 'virtual-good',
    DANGEROUS_GOOD = 'dangerous-good',
}

export enum SHOPIFY_STATUS_ENUM {
    UNFULFILLED = 'UNFULFILLED',
    FULFILLED = 'FULFILLED'
}

export interface IProductRma {
    id: string;
    status: string;
}

export interface IProduct {
    image: {
        alt: string;
        url: string;
    },
    shop_variant_id:string;
    shop_line_item_id: string | null;
    fulfillment_status:string;
    catalog_price: number;
    name: string;
    variantTitle: string;
    quantity: number;
    presentment_money: number;
    discount_amount:number;
    presentment_discount_amount: number;
    discount_compared_at_price_amount_original: number | null;
    discount_compared_at_price_amount_current: number | null;
    paid_price_set: {
        amount: number;
        currency_code: string;
    }
    updatedAt: string;
    fulfilledAt: string;
    variant_url: string;
    /** @deprecated Use `rma.id` instead. Will be removed in a future release. */
    rma_id?: string;
    rma?: IProductRma;
    status?: PRODUCT_STATUS_ENUM;
    _is_hidden: boolean;
    bxgy_group_info?: {
        group:string;
        codenName:string;
        is_buy:boolean;
        is_get:boolean;
    }
}

export interface IOrderRmaHeader {
    list: {id: string;}[],
    total_count: number;
}
export interface IRemovedItemsInfo {
    quantity: number;
}
export interface ISalesChannel {
    name: string;
    image: string;
}
export interface ILocation {
    name: string;
    address?: string | null;
}
export interface IOrder {
    customer_id: string;
    name: string;
    order_id: string;
    products: IProduct[];
    locale?: string;
    customer_email: string;
    payment_currency_code: string;
    store_currency_code: string;
    shipment_customer_address: string;
    removedItemsInfo: IRemovedItemsInfo;
    conversion_rate: number; /** current convertion rate, eg: if it is changing every minute we will see different values every minute */
    sales_channel: ISalesChannel | null;
    location: ILocation | null;
    isCancelled: boolean;
    isRefunded: boolean;
    orderUrl: string;
    parent_order_ids?: string[];
    is_covered?: boolean;
    /** Global-e only, and only within a mixed-order response. Absent for a regular order. */
    glbe_order_id?: string;
    /** The group's major order id. Equal to glbe_order_id on the major order itself. */
    major_order_id?: string;
}
export interface IOutOfPolicyOrderResultDto{
    is_out_of_policy: boolean;
    create_rma_not_allowed: boolean;
    create_rma_not_allowed_reason?: string;
    order_items: {
        line_item_id: string;
        available_policies: number[];
        /** True when the item is a virtual item and the store blocks returns of virtual items. */
        is_return_blocked_virtual?: boolean;
        /** True when the item is flagged as a dangerous good and its return is not allowed. */
        is_return_blocked_dangerous_good?: boolean;
    }[];
}
export interface IFetchOrder{
    order: IOrder,
    rma_list: IOrderRmaHeader;
    error_message?: string;
    eligibility?: IOutOfPolicyOrderResultDto;
    /** Present when the Create RMA search opts in and the back-end emits a complete mixed-order group. */
    mixed_order_group?: IFetchOrder[];
}

export interface IOrderRmaList {
    total_count: number;
    list: {id: string}[];
};
@injectable()
class OrderService {
	private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async fetchOrder(
        orderName: string,
        globalEOrderId: string|null = null,
        eligibility: boolean = false,
        useCache: boolean = false,
        withRefunded = true,
        withMixedOrderGroup = false,
    ): Promise<IFetchOrder> {
        const withMixedOrderGroupParam = withMixedOrderGroup ? '&with_mixed_order_group=true' : '';
        const res = await this.httpService.get<IApiResponse<IFetchOrder>>(
            `/rms-api/fetch-order?order_name=${encodeURIComponent(orderName)}&globale_order_id=${encodeURIComponent(globalEOrderId ?? '')}&use_cache=${useCache}&eligibility=${eligibility}&with_refunded=${withRefunded}${withMixedOrderGroupParam}`,
        );
        return res.payload;
    };

    public async fetchOrderRmasList(id: string): Promise<IOrderRmaList> {
        const res = await this.httpService.get<IApiResponse<IOrderRmaList>>(`/rms-api/order-rmas-list/${id}`);
        return res.payload;
    };
}


export default OrderService;
